import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { MEDIA } from "@/lib/media";

const CHAPTERS = [
  { n: "01", t: <>Learning on <span className="font-semibold">your</span> Country</>, b: "Every landscape becomes part of the curriculum. Local species, seasonal cycles, soils, climate and disturbance history shape what is taught, how it is taught and the practical challenges participants learn to navigate." },
  { n: "02", t: "Why technical skill alone is not enough", b: "Native seed work involves technical skill, but it also demands judgement. Conditions change. Species behave differently. Seasons shift. A method that succeeds in one landscape may fail in another." },
  { n: "03", t: "Learning at a human scale", b: "The Academy brings together ecological science, practical work, thoughtful inquiry and relationship with Country. Learning moves continually between understanding and action, science and lived knowledge, reflection and application." },
  { n: "04", t: "Place-based and regionally adapted", b: "Core principles remain consistent, but their application must be adapted to place. The Academy avoids pretending that one model can be imposed everywhere." },
  { n: "05", t: "Shared knowledge and mutual learning", b: "Participants are not passive recipients of information. They observe, practise, question, compare and apply what they learn to the landscapes and projects for which they are responsible." },
  { n: "06", t: "Reflection, field practice and inquiry", b: "Reflection helps turn experience into judgement. Field journals record observations, timing, methods tried, questions, mistakes and ideas for future work." },
  { n: "07", t: "Capability, not dependency", b: "The aim is practical independence: people who can observe, decide, act and continue learning long after a course has ended." },
  { n: "08", t: "Continuing learning and regional networks", b: "Learning continues through mentoring, project support and connection with others doing the work across regions." },
];

export default function OurApproach() {
  return (
    <div data-testid="approach-page">
      <PageHero
        kicker="Our approach"
        title="Building capability for the landscapes of tomorrow."
        subtitle="Australia is entering a period of unprecedented investment in ecological restoration, biodiversity recovery and climate resilience. The success of that work will depend not only on funding and technology, but on people with the knowledge, judgement and practical skills to restore landscapes well."
        image={MEDIA.landscape}
      />

      <section className="bg-cream pt-24 md:pt-36 pb-8">
        <div className="max-w-[900px] mx-auto px-6 md:px-12 space-y-8">
          <Reveal>
            <p className="font-serif text-3xl md:text-4xl font-light leading-snug text-rainforest">
              SeedKeepers Academy was created to develop that capability.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="text-charcoal/75 text-lg font-light leading-relaxed">
              Our approach combines ecological understanding with hands-on experience, recognising
              that native seed work cannot be learned through theory alone. It is built through
              observation, practice, reflection and time spent on Country, working with real species
              in real conditions.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="text-charcoal/75 text-lg font-light leading-relaxed">
              Every program is designed around the challenges participants will face beyond the
              Academy — whether collecting native seed, establishing a seed bank, supporting
              post-fire recovery, rehabilitating mine sites, restoring habitat or building a native
              seed enterprise.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="text-charcoal/75 text-lg font-light leading-relaxed">
              Rather than simply delivering courses, we work alongside individuals and organisations
              to build lasting capability. Our goal is that every participant leaves not only with
              new skills, but with the confidence, judgement and practical experience to continue the
              work, contribute to their community and strengthen Australia's growing native seed and
              restoration sector.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={(i % 2) * 0.05}>
              <div className="grid grid-cols-1 md:grid-cols-[110px_1fr] gap-6 md:gap-14 items-start border-t border-charcoal/12 py-10">
                <span className="font-serif text-5xl md:text-6xl text-ochre/80 leading-none">{c.n}</span>
                <div>
                  <h2 className="font-serif text-3xl md:text-4xl font-normal text-charcoal mb-4">{c.t}</h2>
                  <p className="text-charcoal/70 text-lg font-light leading-relaxed max-w-2xl">{c.b}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
